import { Dispatch } from 'redux';
import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { withJurisdictionKeys, withLanguage } from '../../utils/aiPreferences';
import {
  ONBOARDING_CHECK_USER_REQUEST,
  ONBOARDING_CHECK_USER_SUCCESS,
  ONBOARDING_CHECK_USER_FAILURE,
  ONBOARDING_CHECK_ORGANIZATION_SUCCESS,
  ONBOARDING_CHECK_ORGANIZATION_FAILURE,
  ONBOARDING_ASSIGN_ORGANIZATION_SUCCESS,
  ONBOARDING_CREATE_ORGANIZATION_SUCCESS,
  ONBOARDING_UPDATE_USER_INFO_SUCCESS,
  ONBOARDING_ASSIGN_ROLE_SUCCESS,
  ONBOARDING_SET_PREFERENCES_SUCCESS,
  ONBOARDING_GET_USER_INFO_SUCCESS,
  ONBOARDING_REQUEST_JOIN_REQUEST,
  ONBOARDING_REQUEST_JOIN_SUCCESS,
  ONBOARDING_REQUEST_JOIN_FAILURE,
  ONBOARDING_CLEAR,
  FETCH_ORG_PEOPLE_REQUEST,
  FETCH_ORG_PEOPLE_SUCCESS,
  FETCH_ORG_PEOPLE_FAILURE,
} from '../types/onboarding';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

export const checkUser = () => async (dispatch: Dispatch<any>) => {
  dispatch({ type: ONBOARDING_CHECK_USER_REQUEST });
  try {
    const response = await apis.get('/onboarding/check-user');
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_CHECK_USER_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to check user');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to check user');
    dispatch({
      type: ONBOARDING_CHECK_USER_FAILURE,
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const checkOrganizationDomain = (email: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_CHECK_ORGANIZATION_REQUEST' });
  try {
    const response = await apis.post('/onboarding/check-organization-domain', { email });
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_CHECK_ORGANIZATION_SUCCESS,
        payload: response.data,
      });
      return response.data.matching_organizations;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to check organization domain');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to check organization domain');
    dispatch({
      type: ONBOARDING_CHECK_ORGANIZATION_FAILURE,
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const requestJoinOrganization = (organizationId: string, requestedRole?: string, message?: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: ONBOARDING_REQUEST_JOIN_REQUEST });
  try {
    const response = await apis.post('/onboarding/request-join-organization', {
      organization_id: organizationId,
      requested_role: requestedRole,
      message: message,
    });
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_REQUEST_JOIN_SUCCESS,
        payload: response.data,
      });
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: response.data.message || 'Join request sent successfully',
          modalType: 'success',
          duration: 3000,
        })
      );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to send join request');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to send join request');
    dispatch({
      type: ONBOARDING_REQUEST_JOIN_FAILURE,
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const assignOrganization = (organizationUuid: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_ASSIGN_ORGANIZATION_REQUEST' });
  try {
    const response = await apis.post('/onboarding/assign-organization', { organization_uuid: organizationUuid });
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_ASSIGN_ORGANIZATION_SUCCESS,
        payload: {
          organization: {
            id: response.data.user?.organization_id,
            uuid: organizationUuid,
            name: response.data.organization?.name || 'Your Organization',
            type: response.data.organization?.type || 'law_firm',
            email_domain: response.data.organization?.email_domain || '',
          },
        },
      });
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: response.data.message || 'Organization assigned successfully',
          modalType: 'success',
          duration: 3000,
        })
      );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to assign organization');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to assign organization');
    dispatch({
      type: 'ONBOARDING_ASSIGN_ORGANIZATION_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const createOrganization = (orgData: {
  name: string;
  type: string;
  email_domains?: string[];  // NEW: array of domains
  email_domain?: string;  // backwards compat (single domain)
  org_details?: Record<string, any>;  // NEW: departments, regions, etc
  parent_id?: string;  // NEW: for hierarchies
  active_license_count?: number;
}) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_CREATE_ORGANIZATION_REQUEST' });
  try {
    // Prepare payload with email_domains (new format) or fallback to email_domain (old format)
    const payload = {
      name: orgData.name,
      type: orgData.type,
      email_domains: orgData.email_domains || (orgData.email_domain ? [orgData.email_domain] : []),
      active_license_count: orgData.active_license_count || 0,
      ...(orgData.org_details && { org_details: orgData.org_details }),
      ...(orgData.parent_id && { parent_id: orgData.parent_id }),
    };

    const response = await apis.post('/onboarding/create-organization', payload);
    if (response.data.success) {
      const organizationPayload = response.data.organization || {
        id: response.data.user?.organization_id,
        uuid: response.data.organization_uuid,
        name: orgData.name,
        type: orgData.type,
        email_domains: orgData.email_domains || (orgData.email_domain ? [orgData.email_domain] : []),
        org_details: orgData.org_details,
        parent_id: orgData.parent_id,
      };

      dispatch({
        type: ONBOARDING_CREATE_ORGANIZATION_SUCCESS,
        payload: {
          ...response.data,
          organization: organizationPayload,
        },
      });
      // dispatch(
      //   showFeedbackModal({
      //     showModal: true,
      //     message: response.data.message || 'Organization created successfully',
      //     modalType: 'success',
      //     duration: 3000,
      //   })
      // );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to create organization');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to create organization');
    dispatch({
      type: 'ONBOARDING_CREATE_ORGANIZATION_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const updateUserInfo = (userData: {
  first_name: string;
  last_name: string;
  role: string;
  email?: string;
  preferences?: Record<string, any>;
}) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_UPDATE_USER_INFO_REQUEST' });
  try {
    // Build payload with email if provided
    const payload = {
      first_name: userData.first_name,
      last_name: userData.last_name,
      role: userData.role,
      ...(userData.email && { email: userData.email }),
    };

    const response = await apis.post('/onboarding/update-user-info', payload);
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_UPDATE_USER_INFO_SUCCESS,
        payload: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          role: userData.role,
          email: userData.email || '',
        },
      });
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: response.data.message || 'User information updated successfully',
          modalType: 'success',
          duration: 3000,
        })
      );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to update user information');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to update user information');
    dispatch({
      type: 'ONBOARDING_UPDATE_USER_INFO_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const assignRole = (role: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_ASSIGN_ROLE_REQUEST' });
  try {
    const response = await apis.post('/onboarding/assign-role', { role });
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_ASSIGN_ROLE_SUCCESS,
        payload: {
          role: response.data.user.role,
        },
      });
      // dispatch(
      //   showFeedbackModal({
      //     showModal: true,
      //     message: response.data.message || 'Role assigned successfully',
      //     modalType: 'success',
      //     duration: 3000,
      //   })
      // );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to assign role');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to assign role');
    dispatch({
      type: 'ONBOARDING_ASSIGN_ROLE_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const setPreferences = (preferences: {
  general_settings?: { language?: string; theme?: string; currency?: string };
  ai_settings?: {
    jurisdiction?: string;
    CountryOfJurisdiction?: string;
    language?: string;
    speaker_voice?: string;
  };
}) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_SET_PREFERENCES_REQUEST' });
  try {
    // Dual-write both jurisdiction keys - onboarding is where the preference
    // is first written, so it must not seed a legacy-only value.
    const body = preferences.ai_settings
      ? {
          ...preferences,
          ai_settings: withLanguage(withJurisdictionKeys(preferences.ai_settings)),
        }
      : preferences;
    const response = await apis.post('/onboarding/set-preferences', body);
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_SET_PREFERENCES_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to set preferences');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to set preferences');
    dispatch({
      type: 'ONBOARDING_SET_PREFERENCES_FAILURE',
      payload: errorMessage,
    });
    throw error;
  }
};

export const completeOnboarding = () => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_COMPLETE_REQUEST' });
  try {
    const response = await apis.post('/auth/onboarding/complete');
    if (response.data.success) {
      dispatch({
        type: 'ONBOARDING_COMPLETE_SUCCESS',
        payload: response.data,
      });
      // dispatch(
      //   showFeedbackModal({
      //     showModal: true,
      //     message: 'Onboarding completed successfully!',
      //     modalType: 'success',
      //     duration: 2000,
      //   })
      // );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to complete onboarding');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to complete onboarding');
    dispatch({
      type: 'ONBOARDING_COMPLETE_FAILURE',
      payload: errorMessage,
    });
    throw error;
  }
};

export const getUserInfo = () => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_GET_USER_INFO_REQUEST' });
  try {
    const response = await apis.get('/onboarding/get-user-info');
    if (response.data.success) {
      dispatch({
        type: ONBOARDING_GET_USER_INFO_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to get user info');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to get user info');
    dispatch({
      type: 'ONBOARDING_GET_USER_INFO_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const verifyInvitation = (invitationToken: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_VERIFY_INVITATION_REQUEST' });
  try {
    const response = await apis.post('/onboarding/verify-invitation', {
      invitation_token: invitationToken,
    });
    if (response.data.success) {
      dispatch({
        type: 'ONBOARDING_VERIFY_INVITATION_SUCCESS',
        payload: response.data,
      });
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to verify invitation');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to verify invitation');
    dispatch({
      type: 'ONBOARDING_VERIFY_INVITATION_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const sendInvitation = (inviteeEmail: string, organizationId: string, role?: string) => async (dispatch: Dispatch<any>) => {
  dispatch({ type: 'ONBOARDING_SEND_INVITATION_REQUEST' });
  try {
    const response = await apis.post('/onboarding/send-invitation', {
      invitee_email: inviteeEmail,
      organization_id: organizationId,
      role: role,
    });
    if (response.data.success) {
      dispatch({
        type: 'ONBOARDING_SEND_INVITATION_SUCCESS',
        payload: response.data,
      });
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: response.data.message || 'Invitation sent successfully',
          modalType: 'success',
          duration: 3000,
        })
      );
      return response.data;
    } else {
      throw new Error(toErrorText(response.data.error) || 'Failed to send invitation');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to send invitation');
    dispatch({
      type: 'ONBOARDING_SEND_INVITATION_FAILURE',
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 5000,
      })
    );
    throw error;
  }
};

export const clearOnboarding = () => (dispatch: Dispatch<any>) => {
  dispatch({ type: ONBOARDING_CLEAR });
};

export const fetchOrgPeople = () => async (dispatch: Dispatch<any>, getState: any) => {
  const state = getState();
  const orgPeople = state.orgPeople;

  // Check if people are already loaded
  if (orgPeople && orgPeople.people && orgPeople.people.length > 0) {
    return orgPeople.people;
  }

  // If already loading, don't make another request
  if (orgPeople && orgPeople.loading) {
    return;
  }

  dispatch({ type: FETCH_ORG_PEOPLE_REQUEST });
  try {
    const response = await apis.get('/org-people/people');
    if (response.data && response.data.people) {
      const people = response.data.people.map((person: any) => ({
        id: person.id,
        email: person.email,
        first_name: person.first_name,
        last_name: person.last_name,
        user_id: person.user_id,
        name: person.name, // Include if it exists in the response
      }));
      dispatch({
        type: FETCH_ORG_PEOPLE_SUCCESS,
        payload: people,
      });
      return people;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error: any) {
    const errorMessage = readErrorMessage(error, 'Failed to load organization people');
    dispatch({
      type: FETCH_ORG_PEOPLE_FAILURE,
      payload: errorMessage,
    });
    dispatch(
      showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000,
      })
    );
    throw error;
  }
};
